import React from 'react';
import { Link } from 'react-router-dom';
import {
  Layers, ArrowLeft, Activity, Brain, Sparkles, Network, Package,
  Users, LineChart, DollarSign, CreditCard, Globe, MessageSquare, Rocket,
  Check, Code,
} from 'lucide-react';
import {
  FLAGSHIP_ENGINES,
  FOUNDATION_GROUPS,
  TOTAL_ENGINES,
  STATUS_COLOR,
  STATUS_LABEL,
} from '../data/enginesCatalog';

const ICON_MAP = {
  Activity, Brain, Sparkles, Network, Package, Users, LineChart, DollarSign,
  CreditCard, Globe, MessageSquare, Rocket, Layers,
};

function StatusBadge({ status }) {
  return (
    <span
      className="inline-block text-[10px] font-mono px-2 py-0.5 rounded"
      style={{ background: `${STATUS_COLOR[status] || STATUS_COLOR.live}20`, color: STATUS_COLOR[status] || STATUS_COLOR.live }}
    >
      {STATUS_LABEL[status] || 'LIVE'}
    </span>
  );
}

export default function EnginesPage() {
  return (
    <div className="min-h-screen bg-[#0A0F1C] text-white">
      <header className="border-b border-[#22304A] bg-[#0D1628]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm text-[#94A3B8] hover:text-white">
            <ArrowLeft className="w-4 h-4" /> Back to Commerce Index
          </Link>
          <div className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-[#FFD166]" />
            <span className="font-semibold">Engines Catalog</span>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-12">
          <h1 className="text-4xl font-bold mb-3">{TOTAL_ENGINES}+ engines, 9 capability groups.</h1>
          <p className="text-lg text-[#94A3B8] max-w-3xl">
            Your full back office, in code. Four flagship product engines you can activate as modules,
            plus the foundation stack that runs underneath every agent, flow, and decision.
          </p>
        </div>

        {/* Flagship product engines */}
        <section className="mb-16">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">Flagship product engines</h2>
            <span className="text-sm text-[#64748B]">Activatable modules</span>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {FLAGSHIP_ENGINES.map(e => {
              const Icon = ICON_MAP[e.icon] || Layers;
              return (
                <div key={e.slug} id={e.slug} className="bg-[#0D1628] border border-[#22304A] rounded-xl p-6">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="p-2 rounded-lg" style={{ background: `${e.color}20` }}>
                      <Icon className="w-5 h-5" style={{ color: e.color }} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-lg" style={{ color: e.color }}>{e.name}</h3>
                        <StatusBadge status={e.status} />
                      </div>
                      <p className="text-sm text-[#94A3B8]">{e.description}</p>
                    </div>
                  </div>
                  {e.subEngines?.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-[#22304A]">
                      <div className="text-xs text-[#64748B] mb-2">Sub-engines</div>
                      <ul className="grid grid-cols-2 gap-1 text-xs text-[#94A3B8]">
                        {e.subEngines.map(s => (
                          <li key={s} className="flex items-center gap-1">
                            <Check className="w-3 h-3 text-[#10B981]" /> {s}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="mt-3 flex items-center gap-1 text-xs text-[#64748B]">
                    <Code className="w-3 h-3" />
                    <span className="font-mono">{e.source}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Foundation engines by group */}
        <section>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">Foundation engines</h2>
            <span className="text-sm text-[#64748B]">9 capability groups · runs under every agent</span>
          </div>
          <div className="space-y-10">
            {FOUNDATION_GROUPS.map(group => {
              const Icon = ICON_MAP[group.icon] || Layers;
              return (
                <div key={group.slug} id={group.slug} className="scroll-mt-20">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-lg" style={{ background: `${group.color}20` }}>
                      <Icon className="w-5 h-5" style={{ color: group.color }} />
                    </div>
                    <h3 className="text-xl font-semibold" style={{ color: group.color }}>{group.name}</h3>
                    <span className="text-xs text-[#64748B]">· {group.engines.length} engines</span>
                  </div>
                  <p className="text-sm text-[#94A3B8] mb-4 ml-12">{group.description}</p>
                  <div className="ml-12 grid md:grid-cols-2 gap-3">
                    {group.engines.map(engine => (
                      <div key={engine.name} className="bg-[#0D1628] border border-[#22304A] rounded-lg p-4">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-white text-sm">{engine.name}</span>
                          <StatusBadge status={engine.status} />
                        </div>
                        <p className="text-xs text-[#94A3B8] mb-2">{engine.desc}</p>
                        <div className="text-[10px] text-[#64748B] font-mono">{engine.source}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <div className="mt-16 pt-8 border-t border-[#22304A] text-center">
          <p className="text-sm text-[#94A3B8] mb-4">
            Every engine runs under the AI CEO (goal_manager) and its 13 directors.
          </p>
          <div className="flex gap-3 justify-center">
            <Link to="/dashboard/org-chart" className="px-4 py-2 border border-[#22304A] rounded-lg text-sm hover:bg-[#11192A]">
              See full org chart →
            </Link>
            <a href="https://commerceindex.ai/agents" target="_blank" rel="noopener noreferrer" className="px-4 py-2 border border-[#22304A] rounded-lg text-sm hover:bg-[#11192A]">
              Agent reputation pages →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
